import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GroupButtons, RewardTypeButtons } from "./manualOrder";

// Number-picker ("numlist" tab) counterpart to manualOrder.jsx's keypad
// ("numpad" tab) — same order-entry screen, same `groups`/`inputType`/
// `garr`/`handleType` data, different bottom widget (a grid of clickable
// numbers here instead of a typed-digit Label). Reuses manualOrder.jsx's
// GroupButtons/RewardTypeButtons directly so both tabs always show the
// exact same lotto-type-driven set of groups and stake codes — this used
// to hardcode a fixed สามตัว/สองตัว/เลขวิ่ง structure that silently
// couldn't offer any stake outside that list (four_top, first, step,
// mix, ...) even when the current lotto type had them configured.
function PanelOrder({
  groups,
  garr,
  inputType,
  setInputType,
  handleType,
  getRate,
  firstnumset,
  numset,
  secondnumset,
  numset2,
  isNumberSelected,
  handleNumberToggle,
  handleNumberGroupToggle,
}) {
  const { t } = useTranslation();
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  useEffect(() => {
    if (selectedGroupId === null && groups.length > 0) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, selectedGroupId]);

  // Switching group drops whatever was selected from the previous group,
  // same as manualOrder.jsx — those codes no longer show up as buttons.
  const handleSelectGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setInputType([]);
  };

  const selectedGroup = groups.find((group) => group.id === selectedGroupId);
  const groupRewardTypes = selectedGroup?.rewardTypes || [];

  // The number grid below is sized (digit count -> max value / padding)
  // to whichever reward type in the CURRENT group is actually toggled on
  // — same derivation manualOrder.jsx uses for its Label's digitCount,
  // generalized from a hardcoded three/two/run split to any stake's own
  // `digit` field. Falls back to the group's first stake so the grid has
  // something sensible to show (disabled) before anything is picked,
  // instead of rendering nothing at all.
  const activeRewardType = groupRewardTypes.find((rt) =>
    inputType.includes(rt.code),
  );
  const fallbackDigit = groupRewardTypes[0]?.digit ?? 0;
  const activeDigit = activeRewardType ? activeRewardType.digit : fallbackDigit;
  const gridEnabled = !!activeRewardType;

  // 3+ digit stakes browse too large a range to list in full (a 4-digit
  // stake is 10,000 numbers) — narrow by first digit first, same UX
  // manualOrder's three-digit case already used. 1-2 digit stakes are
  // small enough to just list every number directly.
  const useFirstDigitFilter = activeDigit >= 3;
  // 4-digit needs a SECOND fixed digit too — first + second digit narrows
  // 10,000 numbers down to the same 100-number range 3-digit's first-digit
  // filter alone gives it, so both land on the same 2-axis grid.
  const useSecondDigitFilter = activeDigit === 4;

  const digits = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

  // Every number cell/checkbox below toggles add/remove in the bet list
  // (see numadd.jsx's isNumberSelected/handleNumberToggle) — a number
  // stays visibly checked for as long as it's actually in `array`.

  // 2/3/4-digit stakes all reduce to a clean 10x10 grid over their LAST two
  // digits — 2-digit has no prefix, 3-digit prefixes with the chosen first
  // digit (`numset`), 4-digit prefixes with first+second digit (`numset` +
  // `numset2`). 1-digit (run_top/run_bot) is the only one small enough
  // (just 10 numbers) that a grid isn't worth it — flat checkbox list.
  const useGrid = activeDigit === 2 || activeDigit === 3 || activeDigit === 4;
  const gridPrefix =
    activeDigit === 3 ? numset : activeDigit === 4 ? `${numset}${numset2}` : "";
  const rowNumbers = (tens) => digits.map((ones) => `${gridPrefix}${tens}${ones}`);
  const colNumbers = (ones) => digits.map((tens) => `${gridPrefix}${tens}${ones}`);
  const allGridNumbers = digits.flatMap(rowNumbers);
  // On a narrow screen, 11 columns (corner + 10 ones-digits) squeezes each
  // cell down to a sliver — split the ones-digit axis into two 5-wide
  // halves (6 columns including the row-header column) stacked vertically
  // instead, so each cell keeps a readable size. Both halves' row-header
  // checkbox still toggles the FULL 10-number row (rowNumbers(tens) is
  // always all 10 ones-digits, not just this half's 5) — same handler,
  // same checked-state check, just rendered twice for reach.
  const colHalves = [digits.slice(0, 5), digits.slice(5, 10)];

  const renderCheckboxGrid = (colDigits, gridColsClass) => (
    <div className={`grid ${gridColsClass} gap-0.5 w-[100%]`}>
      <NumberCheckbox
        checked={allGridNumbers.every((n) => isNumberSelected(n, activeDigit))}
        onClick={() => handleNumberGroupToggle(allGridNumbers, activeDigit)}
      />
      {colDigits.map((ones) => (
        <NumberCheckbox
          key={`col-${ones}`}
          checked={colNumbers(ones).every((n) =>
            isNumberSelected(n, activeDigit),
          )}
          onClick={() =>
            handleNumberGroupToggle(colNumbers(ones), activeDigit)
          }
        />
      ))}
      {digits.map((tens) => (
        <React.Fragment key={`row-${tens}`}>
          <NumberCheckbox
            checked={rowNumbers(tens).every((n) =>
              isNumberSelected(n, activeDigit),
            )}
            onClick={() =>
              handleNumberGroupToggle(rowNumbers(tens), activeDigit)
            }
          />
          {colDigits.map((ones) => {
            const number = `${gridPrefix}${tens}${ones}`;
            return (
              <NumberCheckbox
                key={number}
                label={`${tens}${ones}`}
                checked={isNumberSelected(number, activeDigit)}
                onClick={() => handleNumberToggle(number, activeDigit)}
              />
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );

  // 1-digit (run_top/run_bot) is the only length small enough (10 numbers)
  // that a grid isn't worth it — a flat checkbox row plus a "select all"
  // toggle up front.
  const useFlatList = activeDigit === 1;
  const flatNumbers = useFlatList ? digits : [];

  return (
    <>
      <div className="flex flex-row w-[100%] h-[auto] p-1">
        <GroupButtons
          groups={groups}
          selectedGroupId={selectedGroupId}
          onSelect={handleSelectGroup}
        />
      </div>

      <div className="w-[100%] flex flex-wrap flex-col h-auto justify-center mt-1 text-center bg-gradient-to-br from-brand-blue-900 via-brand-blue-800 to-brand-blue-900 items-center border-2 border-brand-gold-300 rounded-card shadow-card p-2 space-y-1">
        <span className="text-xs font-semibold text-brand-gold-300 tracking-wide">
          {t("numadd.selectedItems")}
        </span>

        <RewardTypeButtons
          rewardTypes={groupRewardTypes}
          inputType={inputType}
          garr={garr}
          handleType={handleType}
          getRate={getRate}
        />

        {useFirstDigitFilter && (
          <div
            id="firstnum"
            className="flex flex-row w-[100%] justify-around gap-1 pt-1"
          >
            {["0", "1", "2", "3", "4", "5", "7", "8", "9"].map((n) => (
              <button
                key={n}
                className="rounded-lg text-[15px] font-bold w-[10%] text-brand-gold-200 shadow-sm bg-gradient-to-b from-brand-blue-500 to-brand-blue-700 hover:from-brand-blue-600 hover:to-brand-blue-800 active:scale-95 transition-all"
                value={n}
                onClick={(e) => firstnumset(e)}
              >
                {n}
              </button>
            ))}
          </div>
        )}

        {useSecondDigitFilter && (
          <div
            id="secondnum"
            className="flex flex-row w-[100%] justify-around gap-1 pt-1"
          >
            {["0", "1", "2", "3", "4", "5", "7", "8", "9"].map((n) => (
              <button
                key={n}
                className="rounded-lg text-[15px] font-bold w-[10%] text-brand-gold-200 shadow-sm bg-gradient-to-b from-brand-blue-500 to-brand-blue-700 hover:from-brand-blue-600 hover:to-brand-blue-800 active:scale-95 transition-all"
                value={n}
                onClick={(e) => secondnumset(e)}
              >
                {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-[100%] h-auto justify-center items-center">
        <div className="div2">
          {useGrid && (
            <div
              id="selectnum"
              className={gridEnabled ? "" : "opacity-30 pointer-events-none"}
            >
              {/* Narrow screens: two 6-column blocks (ones-digit 0-4, then
                  5-9) stacked vertically, so cells stay readable. */}
              <div className="flex flex-col gap-1 sm:hidden">
                {renderCheckboxGrid(colHalves[0], "grid-cols-6")}
                {renderCheckboxGrid(colHalves[1], "grid-cols-6")}
              </div>
              {/* sm and wider: the full 11-column grid, one screen. */}
              <div className="hidden sm:block">
                {renderCheckboxGrid(digits, "grid-cols-11")}
              </div>
            </div>
          )}
          {useFlatList && (
            <div
              id="selectnum"
              className={`flex flex-row flex-wrap gap-0.5 w-[100%] justify-center ${
                gridEnabled ? "" : "opacity-30 pointer-events-none"
              }`}
            >
              <NumberCheckbox
                label={t("numadd.selectAll")}
                checked={flatNumbers.every((n) =>
                  isNumberSelected(n, activeDigit),
                )}
                onClick={() =>
                  handleNumberGroupToggle(flatNumbers, activeDigit)
                }
                wide
              />
              {flatNumbers.map((number) => (
                <NumberCheckbox
                  key={number}
                  label={number}
                  checked={isNumberSelected(number, activeDigit)}
                  onClick={() => handleNumberToggle(number, activeDigit)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// One number/toggle cell — a header cell (no `label`, used for the corner/
// row/column "select all in this group" toggles in the 2-axis grid) or a
// data cell (`label` = the number/text it represents). `wide` widens it
// past the usual small square, used for the flat list's own "select all".
function NumberCheckbox({ label, checked, onClick, wide }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-0.5 rounded p-1 text-[11px] font-bold border transition-colors ${
        wide ? "px-2" : ""
      } ${
        checked
          ? "bg-brand-gold-400 border-brand-gold-200 text-brand-ink-onGold"
          : label
            ? "bg-brand-blue-800/60 border-brand-blue-600 text-brand-gold-100 hover:border-brand-gold-300"
            : "bg-brand-blue-900 border-brand-blue-600 hover:border-brand-gold-300"
      }`}
    >
      <span
        className={`inline-block w-3 h-3 rounded-sm border shrink-0 ${
          checked
            ? "bg-white border-white"
            : "bg-transparent border-brand-gold-200"
        }`}
      >
        {checked && (
          <svg viewBox="0 0 16 16" className="w-3 h-3 text-brand-blue-800">
            <path
              fill="currentColor"
              d="M6.5 11.5 3 8l1-1 2.5 2.5L12 4l1 1z"
            />
          </svg>
        )}
      </span>
      {label}
    </button>
  );
}

export default PanelOrder;
